import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import { oneTimePasswordShape } from 'components/hooks/useOneTimePassword';
import { validationErrorsShape } from 'components/hooks/useValidationErrors';

import FilledButton from 'components/application/buttons/FilledButton';
import TextButton from 'components/application/buttons/TextButton';
import ModalBox from 'components/application/ModalBox';

import OneTimePasswordSignInCard from 'components/users/twoFactorAuthentication/OneTimePasswordSignInCard';
import ValidationErrorsMessages from 'components/validationErrors/components/ValidationErrorsMessages';

export default function DisableModal({
  closeModal,
  isOpen,
  onFormSubmit,
  oneTimePasswordProps,
  validationErrorsProps,
}) {
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const { t: i18nTCommon } = useTranslation('common');

  const handleFormValidate = (otpCode) => {
    // check if the code is valid, but doesn't disable it
    const otpCodeStr = otpCode.join('');
    axios
      .post('/settings/two_factor/validations', { otp_attempt: otpCodeStr })
      .then(() => setIsButtonDisabled(false))
      .catch(() => {
        oneTimePasswordProps.handleCleanupField();
        validationErrorsProps.handleValidationErrorsAssignment({
          otp_attempt: [
            i18nTCommon('authentication.errors.incorrectVerificationCode'),
          ],
        });
      });
  };

  useEffect(() => {
    oneTimePasswordProps.inputRefs[0].current.focus();
  }, [isOpen]);

  return (
    <ModalBox
      customFooter={
        <div className='modalbox__footer-actions modalbox__footer-actions--right'>
          <TextButton
            className='tw-mr-3'
            color='blue'
            onClick={closeModal}
            size='md'
          >
            {i18nTCommon('shared.cancel')}
          </TextButton>
          <FilledButton
            color='red'
            disabled={isButtonDisabled}
            onClick={() => onFormSubmit(oneTimePasswordProps.otpCode)}
          >
            {i18nTCommon('authentication.turnOffTwoFactor')}
          </FilledButton>
        </div>
      }
      isOpen={isOpen}
      mode='flexible'
      onClose={closeModal}
    >
      <h2 className='tw-m-0 tw-px-8 tw-pt-8 tw-text-xl tw-font-semibold tw-text-grey-900'>
        {i18nTCommon('authentication.turnOffTwoFactor')}
      </h2>
      <hr className='tw-mb-0 tw-w-full' />
      {Object.keys(validationErrorsProps.validationErrors).length > 0 && (
        <ValidationErrorsMessages
          className='tw-m-6 tw-border-1 tw-border-solid tw-border-red-200'
          validationErrors={validationErrorsProps.validationErrors}
        />
      )}
      <div className='tw-p-8'>
        <p className='tw-text-sm tw-font-small tw-mb-5 tw-text-center tw-text-grey-700'>
          <Trans
            i18nKey='settings.profile.twoFactorAuthentication.DisableModal.description'
            ns='components'
          />
        </p>
        <OneTimePasswordSignInCard
          inputRefs={oneTimePasswordProps.inputRefs}
          onFormPasteCapture={oneTimePasswordProps.handleFormPasteCapture}
          onInputClick={oneTimePasswordProps.handleInputClick}
          onInputKeyUp={oneTimePasswordProps.handleInputKeyUp}
          onSubmit={handleFormValidate}
          otpCode={oneTimePasswordProps.otpCode}
        />
      </div>
    </ModalBox>
  );
}

DisableModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  oneTimePasswordProps: oneTimePasswordShape,
  onFormSubmit: PropTypes.func.isRequired,
  validationErrorsProps: validationErrorsShape,
};
